import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { CENARIOS_PROJECAO_PADRAO, baseRecorrenteMensal, calcularProjecaoDeBaseReal } from '../../../../../src/lib/dashboard/projecaoFaturamento';
import { agregarPorVencimento } from '../../../../../src/lib/recebiveisUc';
import type { AgendaDaUc } from '../../../../../src/lib/recebiveisUc';
import LoadingSpinner from '../../../../../src/components/LoadingSpinner';

const HORIZONTES = [
  { meses: 1, label: 'Próximo mês' },
  { meses: 3, label: 'Próximos 3 meses' },
  { meses: 6, label: 'Próximos 6 meses' },
  { meses: 12, label: 'Próximos 12 meses' },
];

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });

// Bloco isolado — NÃO recebe os filtros globais do painel (período/departamento)
// de propósito: a pergunta que ele responde ("se não vendermos mais nada")
// é sobre a base recorrente atual, não sobre um recorte de período passado.
interface Props {
  agendas: AgendaDaUc[];
  loading: boolean;
  erro: string | null;
}

const ProjecaoFaturamento: React.FC<Props> = ({ agendas, loading, erro }) => {
  const [horizonteMeses, setHorizonteMeses] = useState(3);

  // A base agora vem da previsão REAL por UC — a mesma do card acima. A conta
  // antiga (kWh contratado × tarifa média) não batia com a realidade por
  // construção: a Vigor fatura energia INJETADA, não consumo contratado.
  const base = useMemo(() => {
    const totais = agregarPorVencimento(agendas);
    return baseRecorrenteMensal(totais.map((t) => t.emitido.valorBoleto + t.projetado.valorBoleto));
  }, [agendas]);

  const resultados = useMemo(
    () =>
      base.valorMensal > 0
        ? CENARIOS_PROJECAO_PADRAO.map((c) => calcularProjecaoDeBaseReal(base.valorMensal, c, horizonteMeses))
        : [],
    [base, horizonteMeses],
  );

  const dadosGrafico = resultados.map((r) => ({
    cenario: `Cenário ${r.cenario.id}`,
    Emitido: Math.round(r.emitidoTotalHorizonte),
    Recebido: Math.round(r.recebidoTotalHorizonte),
  }));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <h2 className="text-sm font-bold text-gray-800 dark:text-white">
        Linha de base estrutural — cenários
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-1 italic">
        "Se a Vigor Energy não vender mais nada, qual a nossa projeção de faturamento?" — baseado no último rateio
        conciliado de cada usina, sem considerar novas vendas. Não segue o período filtrado no topo da tela: o
        horizonte é o do seletor abaixo.
      </p>
      {/* O card "Quanto vou receber", logo acima, responde outra pergunta: um
          MÊS específico, somando a previsão real de cada UC. Este bloco é a
          base recorrente sob cenários e repete o mesmo valor em todos os meses
          do horizonte. Dois números diferentes na mesma tela precisam dizer,
          cada um, de que pergunta são — senão viram contradição aparente. */}
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-4">
        Base recorrente = mediana do "a faturar" dos {base.mesesConsiderados} meses previstos.
        Igual em todos os meses do horizonte. Para o caixa de um mês específico, use o card{' '}
        <strong>Quanto vou receber</strong> acima.
      </p>

      {/* LoadingSpinner, não texto solto: mesmo componente que as outras
          seções do Painel Executivo usam para carregar. */}
      {loading && <LoadingSpinner label="Carregando base da projeção..." />}
      {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}

      {/* Mesmo texto de "sem dado" do card "Quanto vou receber" acima — sem
          isto, base zerada vira toggle de horizonte sobre um gráfico vazio,
          sem nada dizendo por quê. */}
      {!loading && !erro && resultados.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Sem base suficiente para projetar. Isso acontece quando ainda não há faturas emitidas ou
          previstas na carteira.
        </p>
      )}

      {!loading && !erro && resultados.length > 0 && (
        <>
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900/40 rounded-full p-1 w-fit mb-4">
            {HORIZONTES.map((h) => (
              <button
                key={h.meses}
                type="button"
                onClick={() => setHorizonteMeses(h.meses)}
                aria-pressed={horizonteMeses === h.meses}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  horizonteMeses === h.meses ? 'bg-primary text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {h.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {resultados.map((r) => (
              <div key={r.cenario.id} className="rounded-xl border border-gray-100 dark:border-gray-700 p-3">
                <p className="text-xs font-bold text-gray-700 dark:text-gray-200">Cenário {r.cenario.id} — {r.cenario.nome}</p>
                <p className="text-[10px] text-gray-400 mb-2">
                  Churn {(r.cenario.churnPct * 100).toFixed(0)}% · Inadimplência {(r.cenario.inadimplenciaPct * 100).toFixed(0)}%
                </p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {fmtBRL(r.emitidoMensal)} <span className="text-[10px] font-normal text-gray-400">emitido/mês</span>
                </p>
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  {fmtBRL(r.recebidoMensal)} <span className="text-[10px] font-normal text-gray-400">recebido/mês</span>
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Total no horizonte: {fmtBRL(r.recebidoTotalHorizonte)}</p>
              </div>
            ))}
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dadosGrafico} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="cenario" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => fmtBRL(v)} width={70} />
              <Tooltip formatter={(v: number) => fmtBRL(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Emitido" fill="#F6A937" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Recebido" fill="#5F6C37" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

export default ProjecaoFaturamento;
